import { SimpleTextLayer } from '@/model/document2/page/surface/design/layer/simpleTextLayer';
import { Rectangle } from '@/utilityModels/rectangle';
import { StringHelper } from './stringHelper';
import { UnitHelper } from './unitHelper';
import { FontManager } from '@/model/fontManager/fontManager';
import { MathHelper } from './mathHelper';
import { DebugHelper } from './debugHelper';
import { Vector2 } from '@/utilityModels/vector2';
import { UserAgentHelper } from './userAgentHelper';

export class SimpleTextLayerHelper
{
    // フォントウェイトを得る
    public static getFontWeight(fontId : string, bold : boolean, fontManager : FontManager, isRendering = false, isRenderingOnClient = false)
    {
        // 太字の代わりに縁取りを使用する場合はboldをfalseとして扱う
        if(FontManager.UseAlternativeOutlineToBold(fontId)) bold = false;
        var font = fontManager.getFontFamilyById(fontId);
        // ウェイトがない場合はボールド設定を返す
        if(font.weight == null) { return bold ? "bold" : "normal"; }
        // レンダリング中はウェイト数値が無効なので、700以上かボールド設定ならボールド、それ以外ならnormalを返す
        if(isRendering && !isRenderingOnClient) { return (font.weight >= 700 || bold) ? "bold" : "normal"; }
        // ボールド設定がない場合はウェイトを返す
        if(!bold) { return font.weight.toString(); }
        // 700 = bold なので、weightがこの値より大きいならこの値を使い、それ以下のときはboldを指定する。
        return font.weight > 700 ? font.weight.toString() : "bold";
    }

    public static getRenderingTextRectArray(layer : SimpleTextLayer, fontManager : FontManager, start : number, end : number, isAll = false, textSizeCache : { [key: string]: any; } = {}, cacheBaseSize = 1) : Array<
    {
        rect : Rectangle,
        line : number,
        character : number
    }>
    {
        let array : Array<{ rect : Rectangle, line : number, character : number }> = [];
        let min = Math.min(start, end);
        let max = Math.max(start, end);

        // テキストのオリジナル文字列を分割(最後に改行コード追加)
        let allOrgCharacters = layer.text.split('');
        allOrgCharacters.push('\n')
        // テキストの描画用文字列を分割(最後に改行コード追加)
        let allDrawCharacters = allOrgCharacters;
        if(layer.inEdit) allDrawCharacters = layer.edittingText.split('')
        else if(layer.autoNewLine) allDrawCharacters = layer.autoLineText.split('');
        allDrawCharacters.push('\n')
        // オリジナル文字列上のインデックス
        let pos = 0;
        // 位置情報
        let x = layer.x;
        let y = layer.y;
        // 行高さ保持
        let lineHeight = 0;
        // フォント
        let fontSize = layer.autoSize ? layer.autoFontSize : layer.baseTextInfo.fontSize;
        let fontWeight = this.getFontWeight(layer.baseTextInfo.fontId, layer.baseTextInfo.bold, fontManager);
        let fontStyle = layer.baseTextInfo.italic ? "italic" : "normal";
        let fontFamily = fontManager.getFontFamilyName(layer.baseTextInfo.fontId);
        let letterSpacing = layer.horizontalAlignment == 3 ? 0 : UnitHelper.convert(layer.baseTextInfo.letterSpacing, "pt", "mm");
        // 行インデックス(オリジナル文字列上)
        let lineIndex = 0;
        // 高さインデックス(オリジナル文字列上)
        let characterIndex = 0;
        // 1行における文字数保持
        let characterNumberInLine = 0;
        // ブランク文字のサイズ
        let blankSize = StringHelper.calcTextSizeOnSVGPt('あ', fontSize, fontFamily, fontWeight, fontStyle);
        let blankHeight = UnitHelper.convert(blankSize.height, "pt", "mm");
        for(let c = 0; c < allDrawCharacters.length; ++c)
        {
            let chara = allDrawCharacters[c];
            if(chara == '\n')
            {
                // 改行コードだった場合
                // 位置を送る
                let lineWidth = x - layer.x - letterSpacing;
                let lastY = y;
                let lastX = x;
                if(lineHeight <= 0) lineHeight = blankHeight;
                if(c == allDrawCharacters.length - 1) y += lineHeight;
                else y += lineHeight * layer.lineSpacing / 100;
                x = layer.x;
                // 行高さリセット
                lineHeight = 0;
                if(allOrgCharacters[pos] == '\n')
                {
                    // 本来の改行位置だった
                    // 配列に追加
                    if(isAll || (min <= pos && pos < max))
                    {
                        array.push({
                            rect : new Rectangle(lastX, lastY, 0, blankHeight),
                            line : lineIndex,
                            character : characterIndex > 0 ? -1 : 0
                        });
                    }
                    
                    // インデックスを送る
                    pos++;
                    // 行を送る
                    lineIndex++;
                    characterIndex = 0;
                }

                // 水平位置合わせ
                if(layer.horizontalAlignment == 1 || layer.horizontalAlignment == 2)
                {
                    let dx = 0;
                    if(layer.horizontalAlignment == 1) { dx = (layer.width - lineWidth) / 2; }
                    else if(layer.horizontalAlignment == 2) { dx = layer.width - lineWidth; }
                    for(let i = array.length - 1; i >= 0; --i)
                    {
                        if(array[i].rect.y == lastY) array[i].rect.x += dx;
                        else break;
                    }
                }
                else if(layer.horizontalAlignment == 3)
                {
                    if(characterNumberInLine > 1)
                    {
                        let tmpLetterSpacing = (layer.width - lineWidth) / (characterNumberInLine - 1);
                        for(let i = array.length - 1; i >= 0; --i)
                        {
                            if(array[i].rect.y == lastY)
                            {
                                if(array[i].character == -1)
                                {
                                    // 行の終端(改行部分)
                                    array[i].rect.x = layer.x + layer.width;
                                }
                                else
                                {
                                    array[i].rect.x += tmpLetterSpacing * array[i].character;
                                }
                            }
                            else 
                            {
                                break;
                            }
                        }
                    }
                }
                // 1行の文字数をリセット
                characterNumberInLine = 0;
            }
            else
            {
                // 文字だった場合
                let size = { "width": 0, "height": 0};
                if(chara in textSizeCache)
                {
                    // キャッシュを使用する
                    let cacheSize = textSizeCache[chara];
                    size.width = cacheSize.width * fontSize / cacheBaseSize;
                    size.height = cacheSize.height * fontSize / cacheBaseSize;
                }
                else
                {
                    // キャッシュを使用しない
                    let calcSize = StringHelper.calcTextSizeOnSVGPt(chara, fontSize, fontFamily, fontWeight, fontStyle);
                    size.width = calcSize.width;
                    size.height = calcSize.height;
                }
                let textWidth = UnitHelper.convert(size.width, "pt", "mm");
                let textHeight = UnitHelper.convert(size.height, "pt", "mm");
                // 文字横幅倍率補正
                textWidth *= layer.baseTextInfo.letterWidth / 100;
                if(isAll || (min <= pos && pos < max))
                {
                    // 配列追加
                    array.push({
                        rect : new Rectangle(x, y, textWidth, textHeight),
                        line : lineIndex,
                        character : characterIndex
                    });
                }
                // 1行の文字数をカウント
                characterNumberInLine++;
                // ポジションを進める
                x += textWidth + letterSpacing;
                // 行高さ更新
                lineHeight = Math.max(lineHeight, textHeight);
                // インデックスを送る
                pos++;
                // 文字を送る
                characterIndex++;
            }
        }

        // 垂直位置合わせ
        if(layer.verticalAlignment == 1 || layer.verticalAlignment == 2)
        {
            let dy = 0;
            if(layer.verticalAlignment == 1) { dy = (layer.y + layer.height - y) / 2; }
            else if(layer.verticalAlignment == 2) { dy = layer.y + layer.height - y; }
            for(let i = 0; i < array.length; ++i)
            {
                array[i].rect.y += dy;
            }
        }

        return array;
    }

    public static getRenderingTextRectArrayVertical(layer : SimpleTextLayer, fontManager : FontManager, start : number, end : number, isAll = false) : Array<
    {
        rect : Rectangle,
        line : number,
        character : number
    }>
    {
        let array : Array<{ rect : Rectangle, line : number, character : number }> = [];
        let min = Math.min(start, end);
        let max = Math.max(start, end);

        // テキストのオリジナル文字列を分割(最後に改行コード追加)
        let allOrgCharacters = layer.text.split('');
        allOrgCharacters.push('\n')
        // テキストの描画用文字列を分割(最後に改行コード追加)
        let allDrawCharacters = allOrgCharacters;
        if(layer.autoNewLine) allDrawCharacters = layer.autoLineText.split('');
        else if(layer.inEdit) allDrawCharacters = layer.edittingText.split('');
        allDrawCharacters.push('\n')
        // オリジナル文字列上のインデックス
        let pos = 0;
        // 位置情報
        let x = layer.x + layer.width;
        let y = layer.y;
        // 行高さ保持
        let lineWidth = 0;
        // フォント
        let fontSize = layer.autoSize ? layer.autoFontSize : layer.baseTextInfo.fontSize;
        let fontWeight = this.getFontWeight(layer.baseTextInfo.fontId, layer.baseTextInfo.bold, fontManager);
        let fontStyle = layer.baseTextInfo.italic ? "italic" : "normal";
        let fontFamily = fontManager.getFontFamilyName(layer.baseTextInfo.fontId);
        let letterSpacing = layer.horizontalAlignment == 3 ? 0 : UnitHelper.convert(layer.baseTextInfo.letterSpacing, "pt", "mm");
        // 行インデックス(オリジナル文字列上)
        let lineIndex = 0;
        // 高さインデックス(オリジナル文字列上)
        let characterIndex = 0;
        // 1行における文字数保持
        let characterNumberInLine = 0;
        // ブランク文字のサイズ
        let isVertical = UserAgentHelper.getBrowser() != "ie";  // IEだと縦書きのサイズ計算が狂うので対応
        let blankSize = StringHelper.calcTextSizeOnSVGPt('あ', fontSize, fontFamily, fontWeight, fontStyle, isVertical);
        let blankWidth = UnitHelper.convert(blankSize.width, "pt", "mm");
        for(let c = 0; c < allDrawCharacters.length; ++c)
        {
            let chara = allDrawCharacters[c];
            if(chara == '\n')
            {
                // 改行コードだった場合
                // 位置を送る
                let lineHeight = y - layer.y - letterSpacing;
                let lastY = y;
                let lastX = x;
                if(lineWidth <= 0) lineWidth = blankWidth;
                if(c == allDrawCharacters.length - 1) x -= lineWidth;
                else x -= lineWidth * layer.lineSpacing / 100;
                y = layer.y;
                // 行幅リセット
                lineWidth = 0;
                if(allOrgCharacters[pos] == '\n')
                {
                    // 本来の改行位置だった
                    // 配列に追加
                    if(isAll || (min <= pos && pos < max))
                    {
                        array.push({
                            rect : new Rectangle(lastX - blankWidth, lastY, blankWidth, 0),
                            line : lineIndex,
                            character : characterIndex > 0 ? -1 : 0
                        });
                    }
                    
                    // インデックスを送る
                    pos++;
                    // 行を送る
                    lineIndex++;
                    characterIndex = 0;
                }

                // 垂直位置合わせ
                if(layer.horizontalAlignment == 1 || layer.horizontalAlignment == 2)
                {
                    let dy = 0;
                    if(layer.horizontalAlignment == 1) { dy = (layer.height - lineHeight) / 2; }
                    else if(layer.horizontalAlignment == 2) { dy = layer.height - lineHeight; }
                    for(let i = array.length - 1; i >= 0; --i)
                    {
                        if(array[i].rect.x == lastX - blankWidth) array[i].rect.y += dy;
                        else break;
                    }
                }
                else if(layer.horizontalAlignment == 3)
                {
                    if(characterNumberInLine > 1)
                    {
                        let tmpLetterSpacing = (layer.height - lineHeight) / (characterNumberInLine - 1);
                        for(let i = array.length - 1; i >= 0; --i)
                        {
                            if(array[i].rect.x == lastX - blankWidth)
                            {
                                if(array[i].character == -1)
                                {
                                    // 行の終端(改行部分)
                                    array[i].rect.y = layer.y + layer.height;
                                }
                                else
                                {
                                    array[i].rect.y += tmpLetterSpacing * array[i].character;
                                }
                            }
                            else 
                            {
                                break;
                            }
                        }
                    }
                }

                // 1行の文字数をリセット
                characterNumberInLine = 0;
            }
            else
            {
                // 文字だった場合
                var size = UserAgentHelper.getBrowser() == "ie" ?
                    blankSize :
                    StringHelper.calcTextSizeOnSVGPt(chara, fontSize, fontFamily, fontWeight, fontStyle, true);
                let textWidth = UnitHelper.convert(size.width, "pt", "mm");
                let textHeight = UnitHelper.convert(size.height, "pt", "mm");
                if(StringHelper.isHankakuStrictly(chara))
                {
                    let hankakuSize = StringHelper.calcTextSizeOnSVGPt(chara, fontSize, fontFamily, fontWeight, fontStyle);
                    textWidth = UnitHelper.convert(hankakuSize.height, "pt", "mm");
                    textHeight = UnitHelper.convert(hankakuSize.width, "pt", "mm");
                }
                // 文字幅倍率補正
                textHeight *= layer.baseTextInfo.letterWidth / 100;
                if(isAll || (min <= pos && pos < max))
                {
                    // 配列追加
                    array.push({
                        rect : new Rectangle(x - blankWidth, y, textWidth, textHeight),
                        line : lineIndex,
                        character : characterIndex
                    });
                }
                // 1行の文字数をカウント
                characterNumberInLine++;
                // ポジションを進める
                y += textHeight + letterSpacing;
                // 行幅更新
                lineWidth = Math.max(lineWidth, blankWidth);
                // インデックスを送る
                pos++;
                // 文字を送る
                characterIndex++;
            }
        }

        // 水平位置合わせ
        if(layer.verticalAlignment == 1 || layer.verticalAlignment == 2)
        {
            let dx = 0;
            if(layer.verticalAlignment == 1) { dx = (x - layer.x) / 2; }
            else if(layer.verticalAlignment == 2) { dx = x - layer.x ; }
            for(let i = 0; i < array.length; ++i)
            {
                array[i].rect.x -= dx;
            }
        }
        return array;
    }

    // 自動サイズを計算する
    public static calcAutoSize(text : string, fontManager : FontManager, fontId : string, letterSpacing : number, letterWidth : number, lineSpacing : number, width : number, height : number, fontWeight : string, fontStyle : string, lineSizeCache : { [key: string]: any; } = {}, cacheBaseSize = 1)
    {
        // 基準となるテキストサイズ
        var baseFontSizePt = 14.0;

        var fontFamily = fontManager.getFontFamilyName(fontId);
        var letterSpacingMm = UnitHelper.convert(letterSpacing, "pt", "mm");
        var lines = text.split('\n');
        var maxLineWidth = 0;
        var totalLineHeight = 0;
        var maxSpacing = -1000;
        for(var i = 0; i < lines.length; ++i)
        {
            var line = lines[i];
            let lineSize = {"width":0, "height":0};
            if(line in lineSizeCache) {
                // キャッシュを使う
                lineSize.width = lineSizeCache[line].width;
                lineSize.height = lineSizeCache[line].height;
            } else {
                // キャッシュを使わない
                lineSize = StringHelper.calcTextSizeOnSVGPt(line.length > 0 ? line : "", baseFontSizePt, fontFamily, fontWeight, fontStyle);
            }
            lineSize.width = UnitHelper.convert(lineSize.width, "pt", "mm");
            lineSize.height = UnitHelper.convert(lineSize.height, "pt", "mm");
            // 文字横幅補正
            lineSize.width *= letterWidth / 100;
            if(line.length == 0) lineSize.width = 0;
            var spacing = (line.length > 0) ? letterSpacingMm * (line.length - 1) : 0;
            maxSpacing = Math.max(spacing, maxSpacing);
            maxLineWidth = Math.max(lineSize.width, maxLineWidth);
            totalLineHeight += lineSize.height;
            if(i < lines.length - 1) totalLineHeight += lineSize.height * (lineSpacing - 100) / 100;
        }

        var scalew = (width - maxSpacing) / maxLineWidth;
        var scaleh = height / totalLineHeight;
        var scale = Math.min(scalew, scaleh);
        var autoFontSize = baseFontSizePt * scale;

        return MathHelper.clamp(autoFontSize, 1, 720);
    }

    public static calcAutoSizeVertical(text : string, fontManager : FontManager, fontId : string, letterSpacing : number, letterWidth : number, lineSpacing : number, width : number, height : number, fontWeight : string, fontStyle : string)
    {
        // 基準となるテキストサイズ
        var baseFontSizePt = 14.0;

        var fontFamily = fontManager.getFontFamilyName(fontId);
        var letterSpacingMm = UnitHelper.convert(letterSpacing, "pt", "mm");
        var lines = text.split('\n');
        var maxLineHeight = 0;
        var totalLineWidth = 0;
        var maxSpacing = -1000;
        let isVertical = UserAgentHelper.getBrowser() != "ie";  // IEだと縦書きのサイズ計算が狂うので対応

        for(var i = 0; i < lines.length; ++i)
        {
            var line = lines[i];
            var characters = line.split('');
            var lineWidth = 0;
            var lineHeight = 0;
            if(characters.length == 0)
            {
                // 空白行
                var characterSize = StringHelper.calcTextSizeOnSVGPt("あ", baseFontSizePt, fontFamily, fontWeight, fontStyle, isVertical);
                characterSize.width = UnitHelper.convert(characterSize.width, "pt", "mm");
                characterSize.height = UnitHelper.convert(characterSize.height, "pt", "mm");
                lineWidth = characterSize.width;
                lineHeight = 0;
            }
            else
            {
                for(var c = 0; c < characters.length; ++c)
                {
                    var character = characters[c];
                    var characterSize = StringHelper.calcTextSizeOnSVGPt(character, baseFontSizePt, fontFamily, fontWeight, fontStyle, isVertical);
                    characterSize.width = UnitHelper.convert(characterSize.width, "pt", "mm");
                    characterSize.height = UnitHelper.convert(characterSize.height, "pt", "mm");
                    if(StringHelper.isHankakuStrictly(character))
                    {
                        // Swap
                        let hankakuSize = StringHelper.calcTextSizeOnSVGPt(character, baseFontSizePt, fontFamily, fontWeight, fontStyle);
                        characterSize = { width : UnitHelper.convert(hankakuSize.height, "pt", "mm"), height : UnitHelper.convert(hankakuSize.width, "pt", "mm") };
                    }
                    // 文字横幅補正
                    characterSize.height *= letterWidth / 100;
                    // 行高さ追加
                    lineHeight += characterSize.height;
                    // 行幅更新
                    lineWidth = Math.max(lineWidth, characterSize.width);
                }
            }
            var spacing = (characters.length > 0) ? letterSpacingMm * (characters.length - 1) : 0;

            maxSpacing = Math.max(spacing, maxSpacing);
            maxLineHeight = Math.max(lineHeight, maxLineHeight);
            totalLineWidth += lineWidth;
            if(i < lines.length - 1) totalLineWidth += lineWidth * (lineSpacing - 100) / 100;
        }

        var scalew = width / totalLineWidth;
        var scaleh = (height - maxSpacing) / maxLineHeight;
        var scale = Math.min(scalew, scaleh);
        var autoFontSize = baseFontSizePt * scale;

        return MathHelper.clamp(autoFontSize, 1, 720);
    }

    // 折り返し有りの自動フォントサイズを計算する
    // こればっかりは実際に置いてみるしかない
    // fontSize : サイズ
    // autoLineText : 自動改行済みテキスト
    public static calcAutoLineSize(text : string, fontManager : FontManager, fontId : string, letterSpacing : number, letterWidth : number, lineSpacing : number, width : number, height : number, fontWeight : string, fontStyle : string, isVertical : boolean = false, textSizeCache : { [key: string]: any; } = {}, cacheBaseSize = 1)
    {
        var tryFontSize = 1;
        var tryBeforeFontSize = 1;
        var step = 20;
        var lines : string[] = text.split('\n');
        var fontFamily = fontManager.getFontFamilyName(fontId);
        while(true)
        {
            // 仮に配置してサイズを得る
            var tryResult = isVertical? this.tryAutoLineLayoutVertical(tryFontSize, text, fontFamily, letterSpacing, letterWidth, lineSpacing, width, height, fontWeight, fontStyle) :
                this.tryAutoLineLayout(tryFontSize, text, fontFamily, letterSpacing, letterWidth, lineSpacing, width, height, fontWeight, fontStyle, textSizeCache, cacheBaseSize);

            if(tryResult.width > width || tryResult.height > height)
            {
                // レイヤー領域を超えてしまったので、フォントサイズを再設定する
                if(step == 20) { step = 10; }
                else if(step == 10) { step = 5; }
                else if(step == 5) { step = 2; }
                else if(step == 2) { step = 1; }
                else if(step == 1) { step = 0.3; }
                else if(step == 0.3) { step = 0.1; }
                else
                {
                    // 以前のサイズで決定する
                    break;
                }

                // 以前のサイズにstepを加えて再チャレンジする
                tryFontSize = tryBeforeFontSize + step;
            }
            else
            {
                // レイヤー領域を超えない。stepを加えてもう一度計測する
                tryBeforeFontSize = tryFontSize;
                lines = tryResult.lines;
                tryFontSize += step;
            }

            if(tryFontSize <= 1)
            {
                tryFontSize = 1;
                break;
            }

            if(tryFontSize >= 720)
            {
                tryFontSize = 720;
                tryBeforeFontSize = tryFontSize;
                lines = tryResult.lines;
                break;
            }
        }

        // 確定している
        // 配列を文字列にする
        var autoLineText = "";
        for(var i = 0; i < lines.length; ++i)
        {
            if(i > 0) autoLineText += "\n";
            autoLineText += lines[i];
        }
        return { fontSize : tryBeforeFontSize, autoLineText : autoLineText };
    }

    // フォントサイズを指定して、折り返しにしたがって改行済みテキストを得る
    // こればっかりは実際に置いてみるしかない
    // autoLineText : 自動改行済みテキスト
    public static getAutoLineTextWithFixedFontSize(text : string, fontManager : FontManager, fontSize : number, fontId : string, letterSpacing : number, letterWidth : number, lineSpacing : number, width : number, height : number, fontWeight : string, fontStyle : string, isVertical : boolean = false, textSizeCache : { [key: string]: any; } = {}, cacheBaseSize = 1)
    {
        var fontFamily = fontManager.getFontFamilyName(fontId);
        // フォントサイズにしたがって、折り返しで改行した文字列配列を得る
        var tryResult = isVertical? this.tryAutoLineLayoutVertical(fontSize, text, fontFamily, letterSpacing, letterWidth, lineSpacing, width, height, fontWeight, fontStyle) :
                this.tryAutoLineLayout(fontSize, text, fontFamily, letterSpacing, letterWidth, lineSpacing, width, height, fontWeight, fontStyle, textSizeCache, cacheBaseSize);
        var lines = tryResult.lines;

        // 確定している
        // 配列を文字列にする
        var autoLineText = "";
        for(var i = 0; i < lines.length; ++i)
        {
            if(i > 0) autoLineText += "\n";
            autoLineText += lines[i];
        }
        return autoLineText;
    }

    private static tryAutoLineLayout(size : number, text : string, fontFamily : string, letterSpacing : number, letterWidth : number, lineSpacing : number, width : number, height : number, fontWeight : string, fontStyle : string, textSizeCache : { [key: string]: any; } = {}, cacheBaseSize = 1)
    {
        var spacing = UnitHelper.convert(letterSpacing, "pt", "mm");
        var layoutw = 0;
        var layouth = 0;
        var layoutLines : Array<string> = [];
        var tempLine = "";

        var allCharacters = text.split('');
        // 最後に改行コードを付与
        allCharacters.push('\n');
        var curx = 0;
        var cury = 0;
        var maxWidth = 0;
        var maxHeight = 0;
        var isFirstInLine = true;
        for(var i = 0; i < allCharacters.length; ++i)
        {
            var character = allCharacters[i];
            if(character == '\n')
            {
                // 改行コード
                // ここまでを行にする
                layoutLines.push(tempLine);
                tempLine = "";
                // 座標を進める
                maxWidth = Math.max(maxWidth, curx);
                curx = 0;
                if(maxHeight <= 0)
                {
                    var blankSize = StringHelper.calcTextSizeOnSVGPt(" ", size, fontFamily, fontWeight, fontStyle);
                    maxHeight = UnitHelper.convert(blankSize.height, "pt", "mm");
                }
                cury += maxHeight * lineSpacing / 100;
                isFirstInLine = true;
            }
            else
            {
                // 通常の文字
                var canPutSameLine = false;
                let characterSize = { "width": 0, "height" : 0 };
                if(character in textSizeCache)
                {
                    // キャッシュを使用する
                    let cacheSize = textSizeCache[character];
                    characterSize.width = cacheSize.width * size / cacheBaseSize;
                    characterSize.height = cacheSize.height * size / cacheBaseSize;
                }
                else
                {
                    // キャッシュを使用しない
                    let calcSize = StringHelper.calcTextSizeOnSVGPt(character, size, fontFamily, fontWeight, fontStyle);
                    characterSize.width = calcSize.width;
                    characterSize.height = calcSize.height;
                }
                characterSize.width = UnitHelper.convert(characterSize.width, "pt", "mm");
                characterSize.height = UnitHelper.convert(characterSize.height, "pt", "mm");

                characterSize.width *= letterWidth / 100;
                if(isFirstInLine)
                {
                    // 最初の文字は同じ行における
                    canPutSameLine = true;

                    maxHeight = characterSize.height;
                }
                else
                {
                    // 文字サイズをチェック
                    var nextx = curx + characterSize.width;
                    if(tempLine.length > 0) nextx += spacing;
                    // 微妙に計算誤差が出るので吸収する
                    if(nextx - 0.02 <= width) canPutSameLine = true;

                    maxHeight = Math.max(maxHeight, characterSize.height);
                }

                if(canPutSameLine)
                {
                    // 同じ行に置く
                    // 座標を進める
                    if(tempLine.length > 0) curx += spacing;
                    curx += characterSize.width;
                    tempLine += character;
                }
                else
                {
                    // ここまでを行にする
                    layoutLines.push(tempLine);
                    // 次の行に置く
                    tempLine = character;
                    // 座標を進める
                    maxWidth = Math.max(maxWidth, curx);
                    curx = characterSize.width;
                    cury += maxHeight * lineSpacing / 100;
                }

                isFirstInLine = false;
            }
        }

        layouth = cury - maxHeight * (lineSpacing - 100) / 100;
        layoutw = maxWidth;

        return { width : layoutw, height : layouth, lines : layoutLines };
    }

    private static tryAutoLineLayoutVertical(size : number, text : string, fontFamily : string, letterSpacing : number, letterWidth : number, lineSpacing : number, width : number, height : number, fontWeight : string, fontStyle : string)
    {
        var layoutw = 0;
        var layouth = 0;
        var layoutLines : Array<string> = [];
        var tempLine = "";

        var spacing = UnitHelper.convert(letterSpacing, "pt", "mm");

        var allCharacters = text.split('');
        // 最後に改行コードを付与
        allCharacters.push('\n');
        var curx = 0;
        var cury = 0;
        var maxWidth = 0;
        var maxHeight = 0;
        var isFirstInLine = true;
        let isVertical = UserAgentHelper.getBrowser() != "ie";  // IEだと縦書きのサイズ計算が狂うので対応
        for(var i = 0; i < allCharacters.length; ++i)
        {
            var character = allCharacters[i];
            if(character == '\n')
            {
                // 改行コード
                // ここまでを行にする
                layoutLines.push(tempLine);
                tempLine = "";
                // 座標を進める
                maxHeight = Math.max(maxHeight, curx);
                cury = 0;
                if(maxWidth <= 0)
                {
                    var blankSize = StringHelper.calcTextSizeOnSVGPt("あ", size, fontFamily, fontWeight, fontStyle, isVertical);
                    maxWidth = UnitHelper.convert(blankSize.width, "pt", "mm");
                }
                curx -= maxWidth * lineSpacing / 100;
                isFirstInLine = true;
            }
            else
            {
                // 通常の文字
                var canPutSameLine = false;
                var characterSize = StringHelper.calcTextSizeOnSVGPt(character, size, fontFamily, fontWeight, fontStyle, isVertical);
                characterSize.width = UnitHelper.convert(characterSize.width, "pt", "mm");
                characterSize.height = UnitHelper.convert(characterSize.height, "pt", "mm");
                if(StringHelper.isHankakuStrictly(character))
                {
                    // Swap
                    let hankakuSize = StringHelper.calcTextSizeOnSVGPt(character, size, fontFamily, fontWeight, fontStyle);
                    characterSize = { width : UnitHelper.convert(hankakuSize.height, "pt", "mm"), height :  UnitHelper.convert(hankakuSize.width, "pt", "mm") };
                }

                characterSize.height *= letterWidth / 100;
                if(isFirstInLine)
                {
                    // 最初の文字は同じ行における
                    canPutSameLine = true;

                    maxWidth = characterSize.width;
                }
                else
                {
                    // 文字サイズをチェック
                    var nexty = cury + characterSize.height;
                    if(tempLine.length > 0) nexty += spacing;
                    // 誤差吸収
                    if(nexty <= height + 0.004) canPutSameLine = true;

                    maxWidth = Math.max(maxWidth, characterSize.width);
                }

                if(canPutSameLine)
                {
                    // 同じ行に置く
                    // 座標を進める
                    if(tempLine.length > 0) cury += spacing;
                    cury += characterSize.height;
                    tempLine += character;
                }
                else
                {
                    // ここまでを行にする
                    layoutLines.push(tempLine);
                    // 次の行に置く
                    tempLine = character;
                    // 座標を進める
                    maxHeight = Math.max(maxHeight, cury);
                    cury = characterSize.height;
                    curx -= maxWidth * lineSpacing / 100;
                }

                isFirstInLine = false;
            }
        }

        layoutw = - (curx + maxWidth * (lineSpacing - 100) / 100);
        layouth = maxHeight;

        return { width : layoutw, height : layouth, lines : layoutLines };
    }
}
